import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { FC, useCallback, useState } from 'react'

import { IFileSettings } from 'core-components/library/dragNDropFileUploader'
import { MeasureController } from 'core/analytics'
import { IPartnerContact } from 'partner360/features/contacts'

import { ContactImageUploader } from '../contactImageUploader/ContactImageUploader'

import { ContactControls } from './contactControls/ContactControls'
import { ContactDetails } from './contactDetails/ContactDetails'
import { ContactShortBio } from './contactShortBio/ContactShortBio'

import styles from './PartnerContactRow.module.scss'

interface IPartnerContactRowProps {
  isContactEditing?: boolean
  hideControls: boolean
  contact: IPartnerContact
  programType: ProgramTypes
  metrics: MeasureController
  onContactChange: (contact: IPartnerContact) => void
  onEditContact: (contact: IPartnerContact) => void
  onDeleteContact: (contact: IPartnerContact) => void
  onDeleteImage: () => void
  onSaveContact: (contact: IPartnerContact, uploadedImage?: IFileSettings) => Promise<void>
  onCancelEditing: () => void
}

export const PartnerContactRow: FC<IPartnerContactRowProps> = ({
  isContactEditing = false,
  hideControls,
  contact,
  programType,
  metrics,
  onContactChange,
  onEditContact,
  onDeleteContact,
  onDeleteImage,
  onSaveContact,
  onCancelEditing,
}) => {
  const [uploadedImage, setUploadedImage] = useState<IFileSettings>()

  const handleEditContact = useCallback(() => {
    onEditContact(contact)
  }, [contact, onEditContact])

  const handleSaveContact = useCallback(async () => {
    // Pass the cached image to parent (undefined if no image was uploaded)
    await onSaveContact(contact, uploadedImage)

    if (uploadedImage) {
      // Clear the cache after successful save
      setUploadedImage(undefined)
    }
  }, [onSaveContact, contact, uploadedImage])

  const handleDeleteImage = useCallback(() => {
    onDeleteImage()
    setUploadedImage(undefined)
  }, [onDeleteImage])

  const handleCancelEditing = useCallback(() => {
    setUploadedImage(undefined)
    onCancelEditing()
  }, [onCancelEditing])

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <ContactImageUploader
          contact={contact}
          isContactEditing={isContactEditing}
          onImageUpdate={setUploadedImage}
          onDeleteImage={handleDeleteImage}
        />
        <ContactDetails
          contact={contact}
          isContactEditing={isContactEditing}
          programType={programType}
          metrics={metrics}
          onContactChange={onContactChange}
        />
        <ContactShortBio
          contact={contact}
          isContactEditing={isContactEditing}
          onChange={onContactChange}
        />
      </div>

      <ContactControls
        hideControls={hideControls}
        isContactEditing={isContactEditing}
        onEdit={handleEditContact}
        onDelete={() => onDeleteContact(contact)}
        onSave={handleSaveContact}
        onCancelEditing={handleCancelEditing}
      />
    </div>
  )
}
